<template>
  <v-container>
    <div class="title-icon">
      <h1 class="d-flex d-sm-none">
        {{ $t("ebook.instructor.apprentices-courses-mobile") }}
      </h1>
      <h1 class="d-none d-sm-flex">{{ $t("ebook.instructor.apprentices-courses") }}</h1>
      <v-btn icon :href="links.ebook.instructorInfo" target="_blank">
        <v-icon>$sl-question</v-icon>
      </v-btn>
    </div>

    <v-form ref="form" lazy-validation>
      <v-select
        v-model="apprenticeModel"
        :items="books"
        :rules="[rules.required]"
        :label="$t('ebook.apprentice-list-title')"
        item-text="text"
        item-value="bookId"
        outlined
        return-object
        @change="selectedBookId"
      >
        <!-- Person header-->
        <template v-slot:item="data">
          <!-- `<th>Person info and book if more then 1</th>` -->
          <div class="d-flex flex-column select__slot__text">
            <div class="select__slot__text--title">{{ data.item.text }}</div>
            <div v-if="books && books.length > 1" class="select__slot__text--subtitile">
              {{ data.item.createdAt }}
            </div>
          </div>
        </template>
      </v-select>

      <template v-if="book && book.person">
        <div class="overline">{{ $t("fields.apprentice-profession") }}</div>
        <h4 v-if="book && book.professionId">{{ getProfessions(book.professionId) }}</h4>
        <progressLinear
          class="mt-n2"
          :value="
            getPercentage(
              Number(book.bookElementsApprovedCount),
              Number(book.bookElementsMandatoryCount)
            )
          "
        ></progressLinear>

        <p class="pt-2">
          {{
            book.person.firstName +
            " " +
            $t("ebook.education-text2-2") +
            " " +
            book.bookElementsApprovedCount
          }}
          {{ $t("ebook.education-text3") + " " + book.bookElementsMandatoryCount }}
          {{ $t("ebook.education-text4") }}
        </p>

        <p class="pb-2" v-if="daysFromNow>0">
          <i
            >{{ book.person.firstName | filterGenitiveName }}
            {{
              $t("ebook.education-text7-1", {
                msg1: goingDays,
                msg2: lastDate,
                msg3: leftDays,
              })
            }}</i
          >
        </p>
        <p class="pb-2" v-else>
          <i
            >{{ book.person.firstName | filterGenitiveName }}
            {{
              $t("ebook.education-text7-3", {
                msg1: goingDays,
                msg2: lastDate,
                msg3: leftDays,
              })
            }}</i
          >
        </p>
        <template v-if="isFinished(book)">
          <MessageBox class="my-5" type="info" v-if="bookApproved.isApproved">
            <h5>{{ $t("ebook.education-approvebox2-title") }}</h5>
            <p class="pr-2">
              {{
                $t("ebook.education-approvebox3-text", {
                  date: bookApproved.date,
                })
              }}
              {{
                $t("ebook.education-approvebox2-text-clock") + " " + bookApproved.time
              }}.
            </p>
          </MessageBox>
          <MessageBox class="my-5" type="info" v-else-if="canApproveEbook(book)==true">
            <h5>{{ $t("ebook.education-approvebox-title") }}</h5>
            <p class="pr-2">{{ $t("ebook.education-approvebox-text") }}</p>
            <ActionButton
              class="mt-2 mb-4 ml-n3"
              smallerText
              @click="approveBook"
              actionIcon="$sl-like"
              :isDisabled="isImitatingUser()"
              >{{ $t("ebook.education-approvebox-btn") }}</ActionButton
            >
          </MessageBox>
          <MessageBox class="my-5" type="info" v-else>
            <h5>{{ $t('ebook.education-approvebox-title2') }}</h5>
            <p class="pr-2">{{ $t('ebook.education-approvebox-text2') }}</p>            
          </MessageBox>          
        </template>
      </template>
    </v-form>

    <!-- Distance education provider courses list starts here -->
    <DistanceEducationProviderCourses
      class="mt-6"
      v-if="book && book.education"
      :education="book.education"
      :apprenticeName="book.person.firstName"
      @modalToogle="modalToogle"
    />
    <!-- List starts here -->
    <ElementsExpansionPanel
      class="mt-6"
      v-if="book && book.bookElements"
      :elements="book.bookElements"
      :isInstructor="true"
      :apprenticeName="book.person.firstName"
      @elementModalToogle="elementModalClick"
      @modalToogle="modalToogle"
    ></ElementsExpansionPanel>
    <EbookElementInfoModal
      v-if="book && book.person && book.person.firstName"
      :show="openElementModal"
      @close="closeElementModal"
      :element="this.dialogElement"
      :apprenticeName="book.person.firstName"
    ></EbookElementInfoModal>
    <EbookProfessionInfoModal
      v-if="book && book.person && book.person.firstName"
      :show="openModal"
      @close="close"
      :apprenticeName="book.person.firstName"
      :element="this.dialogElement"
    ></EbookProfessionInfoModal>
  </v-container>
</template>

<script>
import API from "@/services/api.js";
import ElementsExpansionPanel from "@/components/Common/Ebook/ElementsExpansionPanel";
import EbookProfessionInfoModal from "@/components/Ebook/Apprentice/EbookProfessionInfoModal";
import EbookElementInfoModal from "@/components/Ebook/EbookElementInfoModal";
import DistanceEducationProviderCourses from "@/components/Ebook/Common/DistanceEducationProviderCourses";
import progressLinear from "@/components/Common/ProgressLinear";
import MessageBox from "@/components/Common/MessageBox.vue";
import ActionButton from "@/components/Common/ActionButton";
import { RulesMixin } from "@/components/mixins";
import { mapGetters } from "vuex";
import get from 'lodash/get'

const SugarDate = require("sugar/date");

// @group Ebook
// Styled ApprenticesCourses component
export default {
  name: "ApprenticesCourses",

  mixins: [RulesMixin],

  components: {
    ElementsExpansionPanel,
    EbookProfessionInfoModal,
    EbookElementInfoModal,
    DistanceEducationProviderCourses,
    progressLinear,
    ActionButton,
    MessageBox,
  },
  data() {
    return {
      links: require('../../../lib/external-links.json'),
      apprenticeModel: null,
      dialogElement: {},
      openModal: false,
      openElementModal: false,
      bookId: null,
      apprentices: [],
      books: [],
      book: {},
      reports: [],
      bookId: null,
      show: true,
      lastDate: "",
      goingDays: "",
      leftDays: "",
      daysFromNow: 0,
      canApprove: false,
      bookApproved: {
        isApproved: false,
        date: '',
        time: '',
        approvedBy: ''
      }
    }
  },  
  created() {
    this.init();
  },
  computed: {
    ...mapGetters("currentuser", [
      "currentUser",
      "isInstructor",
      "isCompanyUser",
      "isOnlyInstructor",
      "isImitatingUser",
    ]),
    ...mapGetters("basic", ["basicProfessions", "hasProfessionOrientations"]),
  },
  filters: {
    filterGenitiveName(name) {
      if (name && name.length > 0) {
        if (name.slice(-1) == "s") return name;
      }

      return name + "s";
    },
  },
  methods: {
    init() {
      if (this.currentUser && this.isCompanyUser(0)) {
        this.getApprenticesList();
      }
    },
    canApproveEbook(b){ 
      let ret =false
        if(!(b && b.apprenticeship && b.apprenticeship.apprenticeshipInstructors && this.currentUser && this.currentUser.employments)){
          return false
        }
        else {
          this.currentUser && this.currentUser.employments.forEach(e => {            
            if(Boolean(b.apprenticeship.apprenticeshipInstructors.find(ap => ap.instructorId ==e.id))){              
                ret=true
            }            
          });
        return ret
      }
    },
    // @vuese
    // Used to check if the all the courses are done
    isFinished(b) {

      let approvedElementsCount = b.bookElementsApprovedCount
      let bookElementsMandatoryCount = b.bookElementsMandatoryCount
      
      // Special case for profession Ställningsbyggare
      if ( b.professionId === 848 ) {
        // If there are no elements, set both approved/mandatory count to 1
        if ( !b.bookElements.length ) {
          approvedElementsCount = 1
          bookElementsMandatoryCount = 1
        } else {
          // If we have elements, then check for element Väderskydd, as it should not be required in this book
          const elementEqualVäderskydd = b.bookElements.find(e => e.elementId === 266)
  
          if( elementEqualVäderskydd ) {
            // If element exist, this element should not be required so we set it as approved
            approvedElementsCount++
            
            // Always set bookElementsMandatoryCount to whatever approvedElementsCount is as they need to be equal
            if ( approvedElementsCount > bookElementsMandatoryCount )
              bookElementsMandatoryCount = approvedElementsCount
          }
        }
      }
      
      if(approvedElementsCount > 0 && approvedElementsCount === bookElementsMandatoryCount)
      { 
        if(b.education && b.education.educationDistanceEducationProviderCourses && b.education.educationDistanceEducationProviderCourses.length>0){
          
          try {
            //- Filter away courses to still be able to approve.
            const ignoredCourses = ['Y4+Y5', 'INTRO']

            let notApproved=Boolean(
              b.education.educationDistanceEducationProviderCourses.filter(course => !ignoredCourses.includes(course.distanceEducationProviderCourse.remoteId)).find(e => e.approved !=true)
            )
            return !notApproved
          } catch (error) {
            console.log(error)
          }
        }
        else {
          return true
        }
      }      
      return false
    },

    hasDateExpired(currentDate, endDate, limit) {
      try {
        const d2 = new Date(endDate)
        const d1 = new Date().setDate(currentDate.getDate() - 1) // Count in the day of end date
        const expirationDate = new Date(d1).setMonth(new Date(d1).getMonth() - limit) // Set max extended date limit to 3 months

        return new Date(d2).getTime() < new Date(expirationDate).getTime()
      } catch (error) {
        console.log('Something went wrong calculating expiration date', error)
      }
    },

    // @vuese
    // Used to get list of books and use the result for the select element as person information

    getApprenticesList() {
      API.listBooks()
        .then((response) => {
          response.data.forEach((obj) => {

            if( this.hasDateExpired(new Date(), obj.education.endDate, 3) ) return

            if (
              this.isOnlyInstructor &&
              obj.apprenticeship &&
              obj.apprenticeship.apprenticeshipInstructors
            ) {
              const myIds = this.currentUser.employments.map((o) => o.id);
              const appIds = obj.apprenticeship.apprenticeshipInstructors.map(
                (o) => o.instructorId
              );
              const found = appIds.some((r) => myIds.includes(r));

              if (found) {
                this.books.push({
                  text:
                    obj.person.firstName +
                    " " +
                    obj.person.lastName +
                    " (" +
                    obj.person.personalNumber +
                    ")",
                  bookId: obj.id,
                  createdAt:
                    this.$t("ebook.button-label") +
                    " " +
                    this.$options.filters.date(obj.createdAt),
                });
              }
            } else {
              this.books.push({
                text:
                  obj.person.firstName +
                  " " +
                  obj.person.lastName +
                  " (" +
                  obj.person.personalNumber +
                  ")",
                bookId: obj.id,
                createdAt:
                  this.$t("ebook.button-label") +
                  " " +
                  this.$options.filters.date(obj.createdAt),
              });
            }

          });
            if ((this.books.length == 1)) {
              this.apprenticeModel = this.books[0];
              this.selectedBookId(this.books[0]);
            }else {
              this.apprenticeModel = null
              this.book=null
            }
        })
        .catch((error) => {
          console.log(error)
          console.log("There was an error:", error.response);
        });
    },

    // @vuese
    // Used to approve book
    // @arg The argument is an integer representing book id
    approveBook() {
      this.$events.fire("showInfo", this.$t("ebook.education-approved-text"));

      API.approveBook(this.book.id)
        .then((res) => {
          if (res.data) {
            this.bookApproved.approvedBy =
              this.currentUser.firstName + " " + this.currentUser.lastName;
            this.bookApproved.isApproved = true;
            this.bookApproved.date = SugarDate.Date(new Date()).format(
              "{yyyy}-{MM}-{dd}"
            ).raw;
            this.bookApproved.time = SugarDate.Date(new Date()).format("{HH}:{mm}").raw;
          }
        })
        .catch((error) => {
          console.log("Error: " + error);
          this.$events.fire("showError", this.$t("errors.save-failure"));
        });
    },
    // @vuese
    // Used to get book by book id
    // @arg The argument is an integer representing book id
    getBookById(bookId) {
      if (!bookId) {
        return;
      }
      API.getBook(bookId)
        .then((res) => {
          if (res.data) {
            this.book = res.data;
            if (
              res.data.approvedByInstructor &&
              res.data.approvedByInstructor.firstName &&
              res.data.approvedByInstructor.lastName
            )
              this.bookApproved.approvedBy =
                res.data.approvedByInstructor.firstName +
                " " +
                res.data.approvedByInstructor.lastName;
            else if (
              res.data.approvedByInstructor &&
              res.data.approvedByInstructor.firstName
            ) {
              this.bookApproved.approvedBy = res.data.approvedByInstructor.firstName;
            }
            
            if (res.data.approvedByInstructorAt) {
              this.bookApproved.isApproved = true;
              this.bookApproved.date = SugarDate.Date(
                res.data.approvedByInstructorAt
              ).format("{yyyy}-{MM}-{dd}").raw;
              this.bookApproved.time = SugarDate.Date(
                res.data.approvedByInstructorAt
              ).format("{HH}:{mm}").raw;
            }

            let isMachineProfession = this.hasProfessionOrientations(res.data.professionId)
            if (isMachineProfession) {
              this.lastDate = SugarDate.Date(get(res, 'data.createdAt', new Date())).addMonths(6).format('{yyyy}-{MM}-{dd}').raw
              const educationEndDate = SugarDate.Date(get(res, 'data.education.endDate', new Date())).format('{yyyy}-{MM}-{dd}').raw
              if ( this.lastDate < educationEndDate )    
                this.lastDate = educationEndDate
            } else {              
              this.lastDate = SugarDate.Date(res.data.createdAt).addMonths(12).format('{yyyy}-{MM}-{dd}').raw
              
              if( SugarDate.Date(res.data.createdAt).addMonths(12).raw < SugarDate.Date(res.data.education.endDate).raw ) {
                this.lastDate = SugarDate.Date(res.data.education.endDate).format('{yyyy}-{MM}-{dd}').raw
              }
            } 

            if (SugarDate.Date(this.book.createdAt).daysAgo() == 1) {
              this.goingDays =
                SugarDate.Date(this.book.createdAt).daysAgo() +
                " " +
                this.$t("ebook.education-day");
            } else {
              this.goingDays =
                SugarDate.Date(this.book.createdAt).daysAgo() +
                " " +
                this.$t("ebook.education-days");
            }
            this.daysFromNow=SugarDate.Date(this.lastDate).addDays(1).daysFromNow()
            if (SugarDate.Date(this.lastDate).addDays(1).daysFromNow() == 1) {
              this.leftDays =
                SugarDate.Date(this.lastDate).addDays(1).daysFromNow() +
                " " +
                this.$t("ebook.education-day");
            } else {
              this.leftDays =
                SugarDate.Date(this.lastDate).addDays(1).daysFromNow() +
                " " +
                this.$t("ebook.education-days");
            }
          }
        })
        .catch((error) => {
          console.log("Store - failed to get user: " + error);
        });
    },
    selectedBookId(book) {
      this.getBookById(book.bookId);
    },
    getProfessions(id) {
      let prof = this.basicProfessions.find((x) => x.id == id);
      if (prof && prof.name) {
        return prof.name;
      }
    },
    elementModalClick(item) {
      this.dialogElement = item;
      this.openElementModal = !this.openElementModal;
    },
    closeElementModal() {
      this.openElementModal = !this.openElementModal;
    },
    modalToogle(item) {
      this.dialogElement = item;
      this.openModal = !this.openModal;
    },

    close() {
      this.openModal = !this.openModal;
    },
    getPercentage(v1, v2) {
      var value = 0;
      if (Number(v2) && v2 > 0) {
        return (v1 / v2) * 100;
      } else return value;
    },
  },
};
</script>

<style scoped></style>
